import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { Activity, Server, Users, Ship, BookOpen, ShoppingCart, Package, Timer, AlertTriangle } from "lucide-react";

export default function AdminHealthPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      const res = await api.getAdminHealth();
      setData(res.data);
      setErr("");
    } catch (e) {
      setErr(e.response?.data?.detail || "Kunne ikke laste");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30000); // auto-refresh every 30s
    return () => clearInterval(t);
  }, []);

  if (user?.role !== "admin") {
    return (
      <div className="max-w-2xl mx-auto p-8 text-center">
        <AlertTriangle className="h-10 w-10 mx-auto text-stone-300 mb-3" />
        <p className="text-stone-600">Kun global admin har tilgang til helse-dashboardet.</p>
      </div>
    );
  }

  if (loading) return <div className="p-8 text-center text-stone-400">Laster…</div>;
  if (err) return <div className="p-8 text-center text-red-600">{err}</div>;
  if (!data) return null;

  const kpi = (label, value, Icon, sub) => (
    <div className="bg-white border border-stone-100 rounded-xl p-4" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g,'-')}`}>
      <div className="flex items-center gap-2 text-stone-400 text-xs uppercase tracking-wider font-semibold">
        <Icon className="h-3.5 w-3.5" /> {label}
      </div>
      <div className="text-2xl font-bold text-stone-900 mt-1" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{value}</div>
      {sub && <div className="text-[11px] text-stone-500 mt-0.5">{sub}</div>}
    </div>
  );

  const errorRate = data.requests_total > 0 ? ((data.errors_total / data.requests_total) * 100).toFixed(2) : "0.00";

  return (
    <div className="space-y-6 max-w-6xl" data-testid="admin-health-page">
      <div>
        <h1 className="text-3xl font-bold text-stone-900" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
          Drift & helse
        </h1>
        <p className="text-sm text-stone-500 mt-1">Oppdateres automatisk hvert 30. sekund</p>
      </div>

      {/* Top KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {kpi("Oppetid", data.uptime_human, Timer, `${data.uptime_seconds}s`)}
        {kpi("Forespørsler", data.requests_total.toLocaleString(), Server, `siden oppstart`)}
        {kpi("Snitt-respons", `${data.avg_response_ms} ms`, Activity, `p95: ${data.p95_response_ms} ms`)}
        {kpi("Feilrate", `${errorRate}%`, AlertTriangle, `${data.errors_total} feil`)}
      </div>

      {/* Counts */}
      <div>
        <h2 className="text-xs uppercase tracking-[0.2em] text-stone-400 font-semibold mb-3">Databaseinnhold</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {kpi("Båter", data.counts.boats, Ship)}
          {kpi("Brukere", data.counts.users, Users)}
          {kpi("Oppskrifter", data.counts.recipes, BookOpen)}
          {kpi("Varer", data.counts.pantry_items, Package)}
          {kpi("Handlelister", data.counts.shopping_lists, ShoppingCart)}
          {kpi("Turer", data.counts.trips, Ship)}
        </div>
      </div>

      {/* Per-boat breakdown */}
      <div>
        <h2 className="text-xs uppercase tracking-[0.2em] text-stone-400 font-semibold mb-3">Per båt</h2>
        <div className="bg-white border border-stone-100 rounded-xl overflow-hidden">
          <table className="w-full text-sm" data-testid="boats-breakdown-table">
            <thead className="bg-sailcloth-100/50 text-left">
              <tr>
                <th className="px-4 py-2 text-xs uppercase tracking-wider text-stone-500 font-semibold">Båt</th>
                <th className="px-4 py-2 text-xs uppercase tracking-wider text-stone-500 font-semibold text-right">Medlemmer</th>
                <th className="px-4 py-2 text-xs uppercase tracking-wider text-stone-500 font-semibold text-right">Varer</th>
                <th className="px-4 py-2 text-xs uppercase tracking-wider text-stone-500 font-semibold text-right">Handlelister</th>
                <th className="px-4 py-2 text-xs uppercase tracking-wider text-stone-500 font-semibold">Siste aktivitet</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {data.boats.map((b) => (
                <tr key={b.id} className="hover:bg-stone-50" data-testid={`health-boat-row-${b.id}`}>
                  <td className="px-4 py-2.5 font-medium text-stone-800">{b.name}</td>
                  <td className="px-4 py-2.5 text-right text-stone-600">{b.member_count}</td>
                  <td className="px-4 py-2.5 text-right text-stone-600">{b.pantry_count}</td>
                  <td className="px-4 py-2.5 text-right text-stone-600">{b.shopping_count}</td>
                  <td className="px-4 py-2.5 text-stone-500 text-xs">{b.last_pantry_date || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
